import { z } from "zod";

const httpUrlSchema = z.url({ protocol: /^https?$/ });
const optionalHttpUrlSchema = z.union([z.literal(""), httpUrlSchema]);

const envSchema = z.object({
  VITE_RECORD_SERVICE_BASE_URL: optionalHttpUrlSchema.default(""),
  VITE_ENGINE_BASE_URL: optionalHttpUrlSchema.default(""),
  VITE_DOCUMENT_SERVICE_BASE_URL: optionalHttpUrlSchema.default(""),
  VITE_SCHEMA_REGISTRY_BASE_URL: optionalHttpUrlSchema.default(""),
  VITE_WORKFLOW_SERVICE_BASE_URL: optionalHttpUrlSchema.default(""),
  /**
   * Notification service (`/ws/notifications`). Leave empty in dev so the
   * socket stays on the page origin and the Vite `/ws` proxy reaches compose
   * port 9047. Deployed pods publish this through `/runtime-config.js`.
   */
  VITE_NOTIFICATION_SERVICE_BASE_URL: optionalHttpUrlSchema.default(""),
  VITE_VERTICAL_PROVISIONER_BASE_URL: optionalHttpUrlSchema.default(""),
  /**
   * Default git coordinates seeded into the flavour-provisioning configure form.
   * Plain strings, NOT http-url validated: a `file:///…` bare repo is a valid
   * provisioning source (local compose mounts one).
   */
  VITE_VERTICAL_PROVISIONER_SOURCE_URL: z
    .string()
    .trim()
    .default("https://github.com/cpes-software/platform-golden-verticals.git"),
  VITE_VERTICAL_PROVISIONER_SOURCE_REF: z.string().trim().default("main"),
  VITE_VERTICAL_PROVISIONER_SOURCE_ROOT: z.string().trim().default("flavours"),
  /**
   * centric-colour-api (colour-media service, local port 9024). Leave empty in dev —
   * requests then go to the same-origin `/centric-colour-api` Vite proxy.
   */
  VITE_COLOUR_API_BASE_URL: optionalHttpUrlSchema.default(""),
  /**
   * centric-expression-api (jq validate/evaluate, local port 9032). Leave empty in
   * dev — requests then go to the same-origin `/centric-expression-api` Vite proxy.
   */
  VITE_EXPRESSION_API_BASE_URL: optionalHttpUrlSchema.default(""),
  VITE_DOCUMENT_BUCKET_NAME: z.string().trim().min(1).default("cpes-documents"),
  /**
   * Document-service identity. Leave BOTH empty to inherit the record-service
   * identity (`VITE_SERVICE_NAME` + `VITE_API_KEY`) — the dev/prod document
   * services accept the same `cpes-admin-portal` credentials. Set both only for
   * a local compose that registers a separate `cpes-document-service` /
   * `DUMMY-123` identity.
   */
  VITE_DOCUMENT_SERVICE_API_KEY: z.string().trim().default(""),
  VITE_DOCUMENT_SERVICE_NAME: z.string().trim().default(""),
  /**
   * centric-vertical-provisioner-api identity. Leave BOTH empty to inherit the
   * app identity (`VITE_API_KEY` / `VITE_SERVICE_NAME`) — cloud/dev registers
   * `cpes-admin-portal` on the provisioner. When those are also empty, fall
   * back to local-compose defaults (`cpes-record-service` + `DUMMY-123`).
   * Set both only for a local compose that registers a separate identity
   * while `VITE_API_KEY` points at something else.
   */
  VITE_VERTICAL_PROVISIONER_API_KEY: z.string().trim().default(""),
  VITE_VERTICAL_PROVISIONER_SERVICE_NAME: z.string().trim().default(""),
  /**
   * centric-colour-api identity. Leave empty for the local-compose defaults
   * (`cpes-record-service` + `DUMMY-123`).
   *
   * NOT the record-service / admin-portal identity: colour-api registers only
   * `cpes-dummy`, `cpes-record-service` and `centric-colour-api`, so sending
   * `cpes-admin-portal` earns `401 API key not found for client` on every call.
   */
  VITE_COLOUR_API_KEY: z.string().trim().default(""),
  VITE_COLOUR_API_SERVICE_NAME: z.string().trim().default(""),
  /**
   * centric-expression-api identity. Leave empty for the local-compose defaults
   * (`cpes-record-service` + `DUMMY-123`). expression-api does not register
   * `cpes-admin-portal`.
   */
  VITE_EXPRESSION_API_KEY: z.string().trim().default(""),
  VITE_EXPRESSION_API_SERVICE_NAME: z.string().trim().default(""),
  VITE_API_KEY: z.string().trim().default(""),
  VITE_SERVICE_NAME: z.string().trim().min(1).default("cpes-admin-portal"),
  VITE_KEYCLOAK_URL: httpUrlSchema.default("http://keycloak.local:8080"),
  VITE_KEYCLOAK_REALM: z.string().trim().min(1).default("master"),
  VITE_KEYCLOAK_CLIENT_ID: z.string().trim().min(1).default("react"),
  VITE_KEYCLOAK_REDIRECT_URI: optionalHttpUrlSchema.optional(),
  VITE_KEYCLOAK_LOGOUT_REDIRECT_URI: optionalHttpUrlSchema.optional(),
  /**
   * Keycloak 26+ parameterized organization scope for the login redirect.
   * Use the org alias (`smb-customer-002`) or full scope (`organization:test-org`).
   * Bare `organization` does not emit claims on local VMS; leave empty only when
   * your IdP adds `organization` without a parameter.
   */
  VITE_KEYCLOAK_ORGANIZATION_SCOPE: z.string().trim().default(""),
  VITE_RELEASE_CREATE_ITEM_CONCURRENCY: z.coerce.number().int().min(1).max(12).default(6),
  VITE_RELEASE_CREATE_LINK_RETRIES: z.coerce.number().int().min(1).max(20).default(8),
  VITE_RELEASE_CREATE_PRELINK_DELAY_MS: z.coerce.number().int().min(0).max(5000).default(300),
  /**
   * "1" wraps the table, record page and composer in a React `<Profiler>` that
   * `scripts/perf-probe.mjs` reads. Off, the wrapper renders nothing extra.
   */
  VITE_PERF_PROBE: z.string().trim().default(""),
});

const rawEnv = {
  VITE_RECORD_SERVICE_BASE_URL: import.meta.env.VITE_RECORD_SERVICE_BASE_URL,
  VITE_ENGINE_BASE_URL: import.meta.env.VITE_ENGINE_BASE_URL,
  VITE_DOCUMENT_SERVICE_BASE_URL: import.meta.env.VITE_DOCUMENT_SERVICE_BASE_URL,
  VITE_SCHEMA_REGISTRY_BASE_URL: import.meta.env.VITE_SCHEMA_REGISTRY_BASE_URL,
  VITE_WORKFLOW_SERVICE_BASE_URL: import.meta.env.VITE_WORKFLOW_SERVICE_BASE_URL,
  VITE_NOTIFICATION_SERVICE_BASE_URL: import.meta.env.VITE_NOTIFICATION_SERVICE_BASE_URL,
  VITE_VERTICAL_PROVISIONER_BASE_URL: import.meta.env.VITE_VERTICAL_PROVISIONER_BASE_URL,
  VITE_VERTICAL_PROVISIONER_SOURCE_URL: import.meta.env.VITE_VERTICAL_PROVISIONER_SOURCE_URL,
  VITE_VERTICAL_PROVISIONER_SOURCE_REF: import.meta.env.VITE_VERTICAL_PROVISIONER_SOURCE_REF,
  VITE_VERTICAL_PROVISIONER_SOURCE_ROOT: import.meta.env.VITE_VERTICAL_PROVISIONER_SOURCE_ROOT,
  VITE_COLOUR_API_BASE_URL: import.meta.env.VITE_COLOUR_API_BASE_URL,
  VITE_EXPRESSION_API_BASE_URL: import.meta.env.VITE_EXPRESSION_API_BASE_URL,
  VITE_DOCUMENT_BUCKET_NAME: import.meta.env.VITE_DOCUMENT_BUCKET_NAME,
  VITE_DOCUMENT_SERVICE_API_KEY: import.meta.env.VITE_DOCUMENT_SERVICE_API_KEY,
  VITE_DOCUMENT_SERVICE_NAME: import.meta.env.VITE_DOCUMENT_SERVICE_NAME,
  VITE_VERTICAL_PROVISIONER_API_KEY: import.meta.env.VITE_VERTICAL_PROVISIONER_API_KEY,
  VITE_VERTICAL_PROVISIONER_SERVICE_NAME: import.meta.env.VITE_VERTICAL_PROVISIONER_SERVICE_NAME,
  VITE_COLOUR_API_KEY: import.meta.env.VITE_COLOUR_API_KEY,
  VITE_COLOUR_API_SERVICE_NAME: import.meta.env.VITE_COLOUR_API_SERVICE_NAME,
  VITE_EXPRESSION_API_KEY: import.meta.env.VITE_EXPRESSION_API_KEY,
  VITE_EXPRESSION_API_SERVICE_NAME: import.meta.env.VITE_EXPRESSION_API_SERVICE_NAME,
  VITE_API_KEY: import.meta.env.VITE_API_KEY,
  VITE_SERVICE_NAME: import.meta.env.VITE_SERVICE_NAME,
  VITE_KEYCLOAK_URL: import.meta.env.VITE_KEYCLOAK_URL,
  VITE_KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM,
  VITE_KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  VITE_KEYCLOAK_REDIRECT_URI: import.meta.env.VITE_KEYCLOAK_REDIRECT_URI,
  VITE_KEYCLOAK_LOGOUT_REDIRECT_URI: import.meta.env.VITE_KEYCLOAK_LOGOUT_REDIRECT_URI,
  VITE_KEYCLOAK_ORGANIZATION_SCOPE: import.meta.env.VITE_KEYCLOAK_ORGANIZATION_SCOPE,
  VITE_RELEASE_CREATE_ITEM_CONCURRENCY: import.meta.env.VITE_RELEASE_CREATE_ITEM_CONCURRENCY,
  VITE_RELEASE_CREATE_LINK_RETRIES: import.meta.env.VITE_RELEASE_CREATE_LINK_RETRIES,
  VITE_RELEASE_CREATE_PRELINK_DELAY_MS: import.meta.env.VITE_RELEASE_CREATE_PRELINK_DELAY_MS,
  VITE_PERF_PROBE: import.meta.env.VITE_PERF_PROBE,
};

const parseEnv = () => {
  const result = envSchema.safeParse(rawEnv);

  if (result.success) {
    return result.data;
  }

  const issues = result.error.issues.map((i) => `  ${i.path.join(".")}: ${i.message}`).join("\n");

  throw new Error(`Environment validation failed:\n${issues}`);
};

export const env = parseEnv();
